import type { PaginatedResponse, PaginationMeta } from "@/types/api";

export interface TotalPaginationPayload {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

export interface TotalPaginatedPayload<T> {
  data: T[];
  pagination: TotalPaginationPayload;
}

function isPositiveInteger(value: unknown): value is number {
  return typeof value === "number" && Number.isInteger(value) && value > 0;
}

function isNonNegativeInteger(value: unknown): value is number {
  return typeof value === "number" && Number.isInteger(value) && value >= 0;
}

export function normalizeTotalPaginatedPayload<T>(
  payload: TotalPaginatedPayload<T>,
  responseName: string,
): PaginatedResponse<T> {
  if (!Array.isArray(payload.data)) {
    throw new TypeError(`${responseName} response must contain a data array.`);
  }

  const { page, pageSize, total, totalPages } = payload.pagination ?? {};
  if (!isPositiveInteger(page) || !isPositiveInteger(pageSize) || !isNonNegativeInteger(total) || !isPositiveInteger(totalPages)) {
    throw new TypeError(`${responseName} response contains invalid pagination metadata.`);
  }

  const expectedTotalPages = Math.max(1, Math.ceil(total / pageSize));
  if (totalPages !== expectedTotalPages) {
    throw new TypeError(`${responseName} response contains inconsistent pagination totals.`);
  }

  const pagination: PaginationMeta = {
    page: Math.min(page, totalPages),
    pageSize,
    totalRecords: total,
    totalPages,
  };

  return { data: payload.data, pagination };
}
